import { useQuery } from '@tanstack/react-query';
import { customInstance } from '@/common/infrastructure/http/apiClient';
import { toSieveParams } from '@/common/infrastructure/http/sieve';
import {
  BLOCKS_LIST_DEFAULT_SORT,
  buildBlocksSieveFilter,
  resolveBlockLifecycleStatus
} from '@/blocks/application/sieve/buildBlocksSieveFilter';
import { resolveBlockKind } from '@/blocks/application/helpers/resolveBlockKind';
import { resolveBlockName } from '@/blocks/domain/helpers/resolveBlockName';
import type { Block, BlockKindFilterValue, BlockStatusFilter } from '@/blocks/domain/types';

interface PdfBlockListItemDto {
  blockId: string;
  blockType?: string;
  kind?: string;
  /** Never blank in a current response (the API defaults it to the kind's display name), but legacy rows may be. */
  name?: string | null;
  description: string | null;
  currentVersionId?: string | number | null;
  draftVersionId?: string | number | null;
  currentVersion?: number;
  status?: string;
  isActive?: boolean;
  isArchived?: boolean;
  archivedAt?: string | null;
  /** Whether the published version a template import copies has a renderable snapshot (null: nothing published). */
  latestPublishedVersionRenderable?: boolean | null;
  createdAt: string;
  updatedAt: string;
}

export function toBlock(dto: PdfBlockListItemDto): Block {
  const status = resolveBlockLifecycleStatus(dto);
  const { kind, rawKind, isSupportedKind } = resolveBlockKind(dto.kind ?? dto.blockType);
  return {
    blockId: String(dto.blockId),
    kind,
    rawKind,
    isSupportedKind,
    name: resolveBlockName(dto.name, { kind, rawKind, isSupportedKind }),
    description: dto.description,
    currentVersionId: dto.currentVersionId != null ? String(dto.currentVersionId) : null,
    draftVersionId: dto.draftVersionId != null && String(dto.draftVersionId).trim() !== '' ? String(dto.draftVersionId) : null,
    currentVersion: dto.currentVersion ?? 0,
    status,
    isActive: dto.isActive ?? status !== 'Archived',
    latestPublishedVersionRenderable: dto.latestPublishedVersionRenderable ?? null,
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt
  };
}

export const blocksQueryKey = ['document-builder', 'blocks'] as const;

export interface UseBlocksArgs {
  page: number;
  pageSize: number;
  search?: string;
  kind?: BlockKindFilterValue;
  statusFilter?: BlockStatusFilter;
  /** When true, always fetch fresh data when the list mounts (Block Library page). */
  reloadOnMount?: boolean;
}

interface BlocksListResponse {
  items: Block[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

interface BlocksListRawResponse extends Omit<BlocksListResponse, 'items'> {
  items: PdfBlockListItemDto[];
}

export const useBlocks = ({
  page,
  pageSize,
  search,
  kind,
  statusFilter = 'all-active',
  reloadOnMount = false
}: UseBlocksArgs): ReturnType<typeof useQuery<BlocksListResponse>> => {
  const queryKey = [...blocksQueryKey, page, pageSize, search ?? '', kind ?? '', statusFilter] as const;

  return useQuery({
    queryKey,
    queryFn: async () => {
      const sieveFilter = buildBlocksSieveFilter({ search, kind, statusFilter });
      const sieveParams = toSieveParams({
        page,
        pageSize,
        filters: sieveFilter || undefined,
        sorts: BLOCKS_LIST_DEFAULT_SORT
      });

      const raw = await customInstance<BlocksListRawResponse>({
        url: '/api/pdf-templates/v1/blocks',
        method: 'GET',
        params:
          statusFilter === 'all'
            ? {
                ...sieveParams,
                includeArchived: true
              }
            : sieveParams
      });

      return {
        ...raw,
        items: (raw.items ?? []).map(toBlock)
      } satisfies BlocksListResponse;
    },
    staleTime: reloadOnMount ? 0 : 1000 * 60 * 5,
    refetchOnMount: reloadOnMount ? 'always' : true
  });
};
